/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Data hooks for Audit Logs.
 */

import { useCallback } from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useApi } from '@/hooks'
import { downloadCsvWithParams } from '@/utils/csvExport'

import { buildAuditQuery, fetchAuditDetail, fetchAuditFacets, fetchAudits } from './api'
import { PAGE_SIZE } from './constants'
import type { AuditFilters, AuditRecord } from './types'

/**
 * The infinite list.
 *
 * `getNextPageParam` is a single expression because the server owns the pagination decision: it
 * returns `nextCursor` until there is nothing left. The client must not reimplement "have we
 * reached the end" from row counts — that logic belongs in exactly one place.
 *
 * The whole filter object is the query key, so a filter change starts a clean fetch and results
 * from two different filter sets can never be mixed together.
 */
export const useAuditLogs = (filters: AuditFilters) => {
  const { api } = useApi()

  const query = useInfiniteQuery({
    queryKey: ['audits', filters],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchAudits(api, filters, { cursor: pageParam, take: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: filters.companyIds.length > 0,
  })

  const rows: AuditRecord[] = query.data?.pages.flatMap((page) => page.data) ?? []
  const namesUnavailable = query.data?.pages.some((page) => page.namesUnavailable) ?? false

  return { ...query, rows, namesUnavailable }
}

/**
 * Aggregates for the result summary and the User filter's options.
 *
 * Kept as its own query so it is cached independently of the list: the list refetches as you
 * scroll, this does not.
 */
export const useAuditFacets = (filters: AuditFilters) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-facets', filters],
    queryFn: () => fetchAuditFacets(api, filters).then((res) => res.data),
    staleTime: 30_000,
    enabled: filters.companyIds.length > 0,
  })
}

/**
 * One record with its full diff, loaded when the user opens it.
 *
 * The list deliberately carries no diff — a page of 50 geofence creations, each embedding a full
 * polygon, would be megabytes.
 */
export const useAuditDetail = (id?: string) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit', id],
    queryFn: () => fetchAuditDetail(api, id!).then((res) => res.data),
    enabled: !!id,
    staleTime: 5 * 60_000,
  })
}

/**
 * Export the current filter set.
 *
 * Generated server-side over everything matching the filters, not the rows that happen to be
 * loaded, and delivered through the app's existing short-lived download-token flow so the browser
 * can follow the URL directly.
 */
export const useAuditExport = (filters: AuditFilters) => {
  const { api } = useApi()

  return useCallback(async () => {
    // URLSearchParams, not an object: companyId and the other multi-value filters must stay as
    // repeated keys for the API to read them.
    await downloadCsvWithParams(api, '/audits/export', buildAuditQuery(filters), 'audit-logs')
  }, [api, filters])
}
